import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { FACEBOOK_POSTING } from '../lib/features'
import { comicPath, formatDate, imageUrl, plural, tagPath } from '../lib/format'
import { useSettings } from '../lib/settings'
import { ArtistComic, ArtistComment, FacebookConnection, InstagramConnection, SiteSettings, TagLayout, TagSummary } from '../lib/types'
import { useImageUploads } from '../lib/useImageUploads'
import { useLoad } from '../lib/useLoad'
import { ComicForm } from '../ui/artist/ComicForm'
import { ComicLinkField } from '../ui/artist/fields'
import { ImageList } from '../ui/artist/ImageList'
import { SocialBadges, SocialDialog } from '../ui/artist/SocialDialog'
import { ComicView } from '../ui/ComicView'
import { Dialog } from '../ui/Dialog'
import { Icon } from '../ui/Icon'
import { Layout } from '../ui/Layout'
import { Alert, EmptyState, Loading, Sk } from '../ui/states'

function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · What a comical life`
  }, [title])
}

/** Artist pages: logged-out visitors go to the login page, and come back after */
function ArtistLayout({ children, wide = true }: { children: ReactNode; wide?: boolean }) {
  const { isArtist } = useAuth()
  const location = useLocation()
  if (!isArtist) {
    try {
      sessionStorage.setItem('comic-site-return-to', location.pathname + location.search)
    } catch {
      // ignore
    }
    return <Navigate to="/login" replace />
  }
  return (
    <Layout footer="artist">
      <div className={wide ? 'column wide' : 'column'}>{children}</div>
    </Layout>
  )
}

function PageHead({ title, intro, action }: { title: string; intro?: string; action?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <div className="eyebrow">Artist tools</div>
        <h1 className="h1">{title}</h1>
        {intro && <p className="lead mt-xs">{intro}</p>}
      </div>
      {action}
    </header>
  )
}

function LoadError({ title, retry }: { title: string; retry: () => void }) {
  return (
    <Alert title={title} actions={<button type="button" className="btn sm" onClick={retry}>Try again</button>}>
      Check your connection and try again.
    </Alert>
  )
}

function FormSkeleton() {
  return (
    <Loading label="Loading comic">
      <div className="vstack gap-lg">
        <Sk w="40%" h={44} />
        <Sk h={160} />
        <Sk h={88} />
        <Sk h={88} />
        <Sk h={48} />
        <Sk h={120} />
      </div>
    </Loading>
  )
}

// ---------- Upload and edit ----------

export function UploadPage() {
  useTitle('Upload a new comic')
  const location = useLocation()
  return (
    <ArtistLayout>
      <ComicForm key={location.key} />
    </ArtistLayout>
  )
}

export function EditPage() {
  useTitle('Edit comic')
  const { id = '' } = useParams()
  const location = useLocation()
  const load = useLoad((signal) => api.get<{ comic: ArtistComic }>(`/api/admin/comics/${id}`, signal), [id])
  return (
    <ArtistLayout>
      {load.state === 'loading' && <FormSkeleton />}
      {load.state === 'error' &&
        (load.error.status === 404 ? (
          <EmptyState title="We couldn't find that comic" actions={<div className="btns"><Link className="btn solid" to="/artist/comics">Go to Manage comics</Link></div>}>
            It may have been deleted.
          </EmptyState>
        ) : (
          <LoadError title="This comic couldn't be loaded" retry={load.retry} />
        ))}
      {load.state === 'ready' && <ComicForm key={load.data.comic.id} initial={load.data.comic} initialMessage={(location.state as { message?: string } | null)?.message} />}
    </ArtistLayout>
  )
}

/** How a draft will look, before it's published */
export function PreviewPage() {
  useTitle('Preview')
  const { id = '' } = useParams()
  const load = useLoad((signal) => api.get<{ comic: ArtistComic }>(`/api/admin/comics/${id}`, signal), [id])
  return (
    <ArtistLayout wide={false}>
      {load.state === 'loading' && <FormSkeleton />}
      {load.state === 'error' && <LoadError title="This comic couldn't be loaded" retry={load.retry} />}
      {load.state === 'ready' && (
        <>
          <div className="artist-bar">
            <span>Preview of a {load.data.comic.status === 'draft' ? 'draft' : 'published comic'}</span>
            <Link className="btn solid sm" to={`/artist/edit/${id}`}>
              Edit comic
            </Link>
          </div>
          <ComicView comic={load.data.comic} preview />
        </>
      )}
    </ArtistLayout>
  )
}

// ---------- Manage comics ----------

type Filter = 'all' | 'published' | 'draft'

export function ManagePage() {
  useTitle('Manage comics')
  const location = useLocation()
  const deleted = (location.state as { deleted?: string } | null)?.deleted
  const [filter, setFilter] = useState<Filter>('all')
  const [posting, setPosting] = useState<ArtistComic | null>(null)
  // Social post results that arrived after the list loaded, by comic id
  const [socialUpdates, setSocialUpdates] = useState<Record<string, Partial<Pick<ArtistComic, 'instagram' | 'facebook'>>>>({})
  const load = useLoad((signal) => api.get<{ items: ArtistComic[] }>('/api/admin/comics', signal), [])

  const newButton = (
    <Link className="btn solid" to="/artist/upload">
      <Icon name="plus" size={18} />
      New comic
    </Link>
  )

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Loading your comics">
        <div className="vstack gap-md">
          {Array.from({ length: 5 }, (_, i) => (
            <Sk key={i} h={98} />
          ))}
        </div>
      </Loading>
    )
  } else if (load.state === 'error') {
    body = <LoadError title="Your comics couldn't be loaded" retry={load.retry} />
  } else if (load.data.items.length === 0) {
    body = (
      <EmptyState headingLevel={2} title="No comics yet" actions={<div className="btns"><Link className="btn solid" to="/artist/upload">Upload a comic</Link></div>}>
        Upload your first comic to get started. You can save it as a draft before publishing.
      </EmptyState>
    )
  } else {
    const list = load.data.items.filter((c) => filter === 'all' || c.status === filter).map((c) => ({ ...c, ...socialUpdates[c.id] }))
    body = (
      <>
        <div role="group" aria-label="Filter comics" className="hstack gap-sm">
          {(
            [
              ['all', 'All'],
              ['published', 'Published'],
              ['draft', 'Drafts'],
            ] as const
          ).map(([value, label]) => (
            <button key={value} type="button" className="tab" aria-pressed={filter === value} onClick={() => setFilter(value)}>
              {label}
            </button>
          ))}
        </div>
        <div aria-live="polite" className="hint">
          {plural(list.length, 'comic')}
        </div>
        {list.length === 0 ? (
          <EmptyState
            headingLevel={2}
            title={filter === 'draft' ? 'No drafts' : 'No published comics'}
            actions={<div className="btns"><button type="button" className="btn solid" onClick={() => setFilter('all')}>Show all comics</button></div>}
          >
            {filter === 'draft' ? 'Everything you have written is published.' : 'Your comics are all drafts for now.'}
          </EmptyState>
        ) : (
          <ul className="list">
            {list.map((c) => {
              const thumb = c.images[c.thumbnailIndex] ?? c.images[0]
              const view = c.status === 'published' ? comicPath(c.slug) : `/artist/preview/${c.id}`
              return (
                <li key={c.id} className="manage-row">
                  <div className="rowthumb">{thumb ? <img src={imageUrl(thumb.key)} alt="" loading="lazy" /> : 'No image'}</div>
                  <div className="info">
                    <Link className="title" to={view}>
                      {c.title || '[Untitled comic]'}
                    </Link>
                    <div className="hstack gap-sm-md">
                      <span className={`pill ${c.status}`}>{c.status === 'published' ? 'Published' : 'Draft'}</span>
                      {c.status === 'published' && <SocialBadges comic={c} />}
                      <span className="hint">Happened {formatDate(c.date)}</span>
                    </div>
                  </div>
                  <div className="btns gap-sm">
                    <Link className="btn sm" to={`/artist/edit/${c.id}`}>
                      Edit
                    </Link>
                    <Link className="btn sm" to={view}>
                      {c.status === 'published' ? 'View' : 'Preview'}
                    </Link>
                    {c.status === 'published' && (
                      <button type="button" className="btn sm" onClick={() => setPosting(c)}>
                        {c.instagram?.status === 'failed' || (FACEBOOK_POSTING && c.facebook?.status === 'failed') ? 'Retry social' : 'Social'}
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </>
    )
  }

  return (
    <ArtistLayout>
      <div className="stack">
        <PageHead title="Manage comics" action={newButton} />
        {deleted && (
          <Alert tone="ok" title="Comic deleted.">
            "{deleted}" and its images were deleted for good.
          </Alert>
        )}
        {body}
      </div>
      {posting && (
        <SocialDialog
          comic={posting}
          onClose={() => setPosting(null)}
          onChange={(platform, post) => setSocialUpdates((all) => ({ ...all, [posting.id]: { ...all[posting.id], [platform]: post } }))}
        />
      )}
    </ArtistLayout>
  )
}

// ---------- Tags ----------

const LAYOUTS: Array<{ value: TagLayout; label: string; hint: string }> = [
  { value: 'stack', label: 'Stacked posts', hint: 'Full comics one after another, like the home page.' },
  { value: 'grid', label: 'Thumbnail grid', hint: 'Small previews with titles, like Related comics.' },
]

function tagFacts(t: TagSummary): string {
  const facts = [plural(t.count, 'comic') + (t.drafts ? ` (${plural(t.drafts, 'draft')})` : '')]
  if (t.description) facts.push('Description')
  facts.push(t.layout === 'grid' ? 'Thumbnail grid' : 'Stacked posts')
  return facts.join(' · ')
}

type TagSaved = { name: string; merged: boolean; comicsChanged: number }

function TagDialog({ tag, onClose, onSaved }: { tag: TagSummary; onClose: () => void; onSaved: (from: string, result: TagSaved) => void }) {
  const [name, setName] = useState(tag.name)
  const [hasDescription, setHasDescription] = useState(Boolean(tag.description))
  const [description, setDescription] = useState(tag.description)
  const [layout, setLayout] = useState<TagLayout>(tag.layout)
  const [nameError, setNameError] = useState('')
  const [failure, setFailure] = useState('')
  // Set when the new name is already a tag, to confirm merging
  const [mergeInto, setMergeInto] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async (merge = false) => {
    setNameError('')
    setFailure('')
    if (!name.trim()) {
      setNameError('Enter a tag name.')
      return
    }
    setBusy(true)
    try {
      const result = await api.put<TagSaved>(`/api/admin/tags/${encodeURIComponent(tag.name)}`, {
        name: name.trim(),
        description: hasDescription ? description : '',
        layout,
        merge,
      })
      onSaved(tag.name, result)
    } catch (error) {
      setBusy(false)
      if (error instanceof ApiError && error.status === 409) {
        setMergeInto(error.message.match(/"(.+)"/)?.[1] ?? name.trim())
      } else if (error instanceof ApiError && error.fields.name) {
        setNameError(error.fields.name)
      } else {
        setFailure(error instanceof ApiError && error.status >= 400 && error.status < 500 ? error.message : 'Your changes are still here. Check your connection and try again.')
      }
    }
  }

  if (mergeInto) {
    return (
      <Dialog labelledBy="tm-title" alert onClose={() => !busy && onClose()}>
        <h2 id="tm-title">Merge into "{mergeInto}"?</h2>
        <p>
          There's already a tag called "{mergeInto}". Merging replaces "{tag.name}" with "{mergeInto}" on {plural(tag.count, 'comic')}. "{mergeInto}" keeps its own
          description and layout.
        </p>
        {failure && (
          <div className="ferr" role="alert">
            <Icon name="alert" size={16} />
            {failure}
          </div>
        )}
        <div className="btns end mt-sm">
          <button type="button" className="btn" disabled={busy} onClick={() => setMergeInto('')}>
            Back
          </button>
          <button type="button" className="btn solid" disabled={busy} onClick={() => save(true)}>
            {busy && <span className="spin" aria-hidden="true" />}
            Merge tags
          </button>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog labelledBy="te-title" onClose={() => !busy && onClose()}>
      <h2 id="te-title">Edit tag</h2>
      <form
        className="vstack gap-lg"
        onSubmit={(e) => {
          e.preventDefault()
          save()
        }}
      >
        <div className="formfield">
          <label htmlFor="te-name" className="lbl">
            Name
          </label>
          <input
            id="te-name"
            className={nameError ? 'field err' : 'field'}
            value={name}
            maxLength={60}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? 'te-name-err' : 'te-name-hint'}
          />
          {nameError ? (
            <div id="te-name-err" className="ferr" role="alert">
              <Icon name="alert" size={16} />
              {nameError}
            </div>
          ) : (
            <div id="te-name-hint" className="hint">
              A new name changes it on all {plural(tag.count, 'comic')} and in the sidebar.
            </div>
          )}
        </div>

        <div className="vstack gap-sm">
          <label className="hstack gap-sm check">
            <input type="checkbox" checked={hasDescription} disabled={busy} onChange={(e) => setHasDescription(e.target.checked)} />
            <strong>Show a description on the tag's page</strong>
          </label>
          {hasDescription && (
            <div className="formfield">
              <label htmlFor="te-desc" className="lbl">
                Description
              </label>
              <textarea id="te-desc" className="field medium" value={description} maxLength={2000} disabled={busy} onChange={(e) => setDescription(e.target.value)} aria-describedby="te-desc-hint" />
              <div id="te-desc-hint" className="hint">
                Shown under the tag's name. Leave a blank line between paragraphs.
              </div>
            </div>
          )}
        </div>

        <fieldset className="plain">
          <legend className="lbl">Page layout</legend>
          {LAYOUTS.map((l) => (
            <label key={l.value} className="opt">
              <input type="radio" name="te-layout" value={l.value} checked={layout === l.value} disabled={busy} onChange={() => setLayout(l.value)} />
              <span>
                <strong>{l.label}</strong>
                <span className="hint">{l.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {failure && (
          <div className="ferr" role="alert">
            <Icon name="alert" size={16} />
            {failure}
          </div>
        )}
        <div className="btns end">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn solid" disabled={busy}>
            {busy && <span className="spin" aria-hidden="true" />}
            Save
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export function ManageTagsPage() {
  useTitle('Manage tags')
  const { reload } = useSettings()
  const [editing, setEditing] = useState<TagSummary | null>(null)
  const [done, setDone] = useState('')
  const load = useLoad((signal) => api.get<{ items: TagSummary[] }>('/api/admin/tags', signal), [])

  const saved = (from: string, r: TagSaved) => {
    setEditing(null)
    if (r.merged) setDone(`"${from}" was merged into "${r.name}" on ${plural(r.comicsChanged, 'comic')}.`)
    else if (r.name !== from) setDone(`"${from}" was renamed to "${r.name}" on ${plural(r.comicsChanged, 'comic')}.`)
    else setDone(`"${r.name}" was saved.`)
    load.retry()
    reload()
  }

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Loading your tags">
        <div className="vstack gap-md">
          {Array.from({ length: 5 }, (_, i) => (
            <Sk key={i} h={74} />
          ))}
        </div>
      </Loading>
    )
  } else if (load.state === 'error') {
    body = <LoadError title="Your tags couldn't be loaded" retry={load.retry} />
  } else if (load.data.items.length === 0) {
    body = (
      <EmptyState headingLevel={2} title="No tags yet" actions={<div className="btns"><Link className="btn solid" to="/artist/comics">Go to Manage comics</Link></div>}>
        Add tags to a comic when you upload or edit it. They'll show up here.
      </EmptyState>
    )
  } else {
    body = (
      <>
        <div aria-live="polite" className="hint">
          {plural(load.data.items.length, 'tag')}
        </div>
        <ul className="list">
          {load.data.items.map((t) => (
            <li key={t.name} className="row">
              <div className="grow vstack gap-xs">
                <Link className="strong text-md" to={tagPath(t.name)}>
                  {t.name}
                </Link>
                <span className="hint">{tagFacts(t)}</span>
              </div>
              <button type="button" className="btn sm" aria-label={`Edit ${t.name}`} onClick={() => setEditing(t)}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <ArtistLayout>
      <div className="stack">
        <PageHead title="Manage tags" intro="Rename a tag, add a description to its page, or choose how its page looks." />
        {done && (
          <Alert tone="ok" title="Tag saved.">
            {done}
          </Alert>
        )}
        {body}
      </div>
      {editing && <TagDialog tag={editing} onClose={() => setEditing(null)} onSaved={saved} />}
    </ArtistLayout>
  )
}

// ---------- Comments ----------

export function CommentsPage() {
  useTitle('Comments')
  const [tab, setTab] = useState<'waiting' | 'approved'>('waiting')
  const [message, setMessage] = useState('')
  const [failure, setFailure] = useState('')
  const load = useLoad((signal) => api.get<{ items: ArtistComment[] }>('/api/admin/comments', signal), [])

  const act = async (c: ArtistComment, action: 'approve' | 'delete') => {
    if (load.state !== 'ready') return
    setFailure('')
    try {
      if (action === 'approve') await api.post(`/api/admin/comments/${encodeURIComponent(c.id)}/approve`)
      else await api.del(`/api/admin/comments/${encodeURIComponent(c.id)}`)
      load.setData({
        items:
          action === 'approve'
            ? load.data.items.map((x) => (x.id === c.id ? { ...x, status: 'approved' } : x))
            : load.data.items.filter((x) => x.id !== c.id),
      })
      setMessage(action === 'approve' ? 'Comment approved. It now shows on the comic page.' : 'Comment deleted.')
    } catch (error) {
      setMessage('')
      setFailure(error instanceof ApiError && error.status ? error.message : 'Check your connection and try again.')
    }
  }

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Loading comments">
        <div className="vstack gap-md">
          <Sk h={150} />
          <Sk h={150} />
        </div>
      </Loading>
    )
  } else if (load.state === 'error') {
    body = <LoadError title="Comments couldn't be loaded" retry={load.retry} />
  } else {
    const waiting = load.data.items.filter((c) => c.status === 'waiting')
    const approved = load.data.items.filter((c) => c.status === 'approved')
    const shown = tab === 'waiting' ? waiting : approved
    body = (
      <>
        <div role="group" aria-label="Filter comments" className="hstack gap-sm">
          <button type="button" className="tab" aria-pressed={tab === 'waiting'} onClick={() => setTab('waiting')}>
            Waiting for review ({waiting.length})
          </button>
          <button type="button" className="tab" aria-pressed={tab === 'approved'} onClick={() => setTab('approved')}>
            Approved ({approved.length})
          </button>
        </div>
        {shown.length === 0 ? (
          <EmptyState icon="chat" headingLevel={2} title={tab === 'waiting' ? 'No comments waiting' : 'No approved comments yet'}>
            {tab === 'waiting' ? 'New comments will show up here, and you will get an email for each one.' : 'Comments you approve show here and on their comic pages.'}
          </EmptyState>
        ) : (
          <ul className="list">
            {shown.map((c) => (
              <li key={c.id} className="card vstack gap-md">
                <div className="hstack baseline gap-xs-md">
                  <strong className="text-md">{c.name}</strong>
                  <span className="hint">{c.email}</span>
                  <span className="hint">{formatDate(c.createdAt)}</span>
                </div>
                <div className="hint">On {c.comicSlug ? <Link to={comicPath(c.comicSlug)}>{c.comicTitle}</Link> : c.comicTitle}</div>
                <p className="body-text sm">
                  {c.text}
                </p>
                <div className="btns gap-sm">
                  {c.status === 'waiting' && (
                    <button type="button" className="btn solid sm" onClick={() => act(c, 'approve')}>
                      Approve
                    </button>
                  )}
                  <button type="button" className="btn danger sm" onClick={() => act(c, 'delete')}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </>
    )
  }

  return (
    <ArtistLayout>
      <div className="stack">
        <PageHead title="Comments" intro="New comments wait here until you approve them. You get an email for each one." />
        {message && <Alert tone="ok" title={message} />}
        {failure && <Alert title="That didn't work.">{failure}</Alert>}
        {body}
      </div>
    </ArtistLayout>
  )
}

// ---------- Newsletter ----------

interface NewsletterComic {
  id: string
  title: string
  date: string
  images: { key: string; altText: string }[]
  thumbnailIndex: number
  /** Published since the last newsletter and not in one yet, so it starts ticked */
  suggested: boolean
  lastSentAt: string | null
}

interface SentNewsletter {
  sentAt: string
  subject: string
  recipients: number
  comics: number
}

interface NewsletterInfo {
  subscribers: number
  emailEnabled: boolean
  /** Newest first */
  sent: SentNewsletter[]
  comics: NewsletterComic[]
}

const COMICS_SHOWN = 12
const DRAFT_KEY = 'comic-site-newsletter-draft'

function useNewsletterInfo() {
  return useLoad((signal) => api.get<NewsletterInfo>('/api/admin/newsletter', signal), [])
}

function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="backlink" to={to}>
      <Icon name="left" size={18} />
      {children}
    </Link>
  )
}

function NewsletterStats({ subscribers, lastSent }: { subscribers: number; lastSent?: SentNewsletter }) {
  return (
    <div className="hstack gap-lg">
      <div className="stat">
        <span className="hint">Confirmed subscribers</span>
        <strong>{subscribers}</strong>
      </div>
      <div className="stat">
        <span className="hint">Last newsletter</span>
        {lastSent ? (
          <>
            <strong>{formatDate(localDay(lastSent.sentAt))}</strong>
            <span className="hint">
              "{lastSent.subject}" to {plural(lastSent.recipients, 'subscriber')}
            </span>
          </>
        ) : (
          <strong>None yet</strong>
        )}
      </div>
    </div>
  )
}

function EmailNotReady() {
  return (
    <Alert tone="info" title="Email isn't set up yet">
      Newsletters can be sent once the whatacomicallife.com email domain is verified.
    </Alert>
  )
}

function NewsletterSkeleton() {
  return (
    <Loading label="Loading the newsletter">
      <div className="vstack gap-md">
        <Sk h={96} />
        <Sk h={300} />
      </div>
    </Loading>
  )
}

/** The list of sent newsletters */
export function NewsletterPage() {
  useTitle('Newsletter')
  const location = useLocation()
  // Set by the new-newsletter page right after sending
  const justSent = (location.state as { sent?: SentNewsletter } | null)?.sent
  const load = useNewsletterInfo()

  const newButton = (
    <Link className="btn solid" to="/artist/newsletter/new">
      <Icon name="plus" size={18} />
      New newsletter
    </Link>
  )

  let body
  if (load.state === 'loading') {
    body = <NewsletterSkeleton />
  } else if (load.state === 'error') {
    body = <LoadError title="Your newsletters couldn't be loaded" retry={load.retry} />
  } else {
    const info = load.data
    // A newsletter is only saved once it has reached everyone, so the one just sent may not be listed yet
    const pending =
      justSent && !info.sent.some((n) => n.subject === justSent.subject && Date.parse(n.sentAt) > Date.parse(justSent.sentAt) - 10 * 60000) ? justSent : null
    body = (
      <>
        <NewsletterStats subscribers={info.subscribers} lastSent={pending ?? info.sent[0]} />
        {!info.emailEnabled && <EmailNotReady />}
        {info.sent.length || pending ? (
          <section className="vstack gap-md">
            <h2 className="h3">Sent newsletters</h2>
            <ul className="list">
              {pending && (
                <li className="manage-row">
                  <div className="info">
                    <strong>{pending.subject || '[No subject]'}</strong>
                    <span className="hint">
                      Sending now · to {plural(pending.recipients, 'subscriber')} · {plural(pending.comics, 'comic')}
                    </span>
                  </div>
                </li>
              )}
              {info.sent.map((n) => {
                const view = `/artist/newsletter/sent/${encodeURIComponent(n.sentAt)}`
                return (
                  <li key={n.sentAt} className="manage-row">
                    <div className="info">
                      <Link className="title" to={view}>
                        {n.subject || '[No subject]'}
                      </Link>
                      <span className="hint">
                        {formatDate(localDay(n.sentAt))} · to {plural(n.recipients, 'subscriber')} · {plural(n.comics, 'comic')}
                      </span>
                    </div>
                    <Link className="btn sm" to={view}>
                      View
                    </Link>
                  </li>
                )
              })}
            </ul>
          </section>
        ) : (
          <EmptyState icon="mail" headingLevel={2} title="No newsletters yet" actions={<div className="btns">{newButton}</div>}>
            Newsletters you send will be listed here.
          </EmptyState>
        )}
      </>
    )
  }

  return (
    <ArtistLayout>
      <div className="stack">
        <PageHead title="Newsletter" intro="Everything you've sent to your subscribers, newest first." action={newButton} />
        {justSent && (
          <Alert tone="ok" title={`Newsletter sent to ${plural(justSent.recipients, 'subscriber')}.`}>
            It can take a few minutes to reach everyone.
          </Alert>
        )}
        {body}
      </div>
    </ArtistLayout>
  )
}

export function NewNewsletterPage() {
  useTitle('New newsletter')
  const load = useNewsletterInfo()
  return (
    <ArtistLayout>
      <div className="stack">
        <BackLink to="/artist/newsletter">All newsletters</BackLink>
        {load.state === 'loading' && <NewsletterSkeleton />}
        {load.state === 'error' && <LoadError title="The newsletter screen couldn't be loaded" retry={load.retry} />}
        {load.state === 'ready' && <NewsletterForm info={load.data} />}
      </div>
    </ArtistLayout>
  )
}

interface NewsletterDraft {
  subject: string
  body: string
  picked: string[]
}

function readDraft(): NewsletterDraft | null {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null')
    return d && typeof d.subject === 'string' && typeof d.body === 'string' && Array.isArray(d.picked) ? d : null
  } catch {
    return null
  }
}

function writeDraft(draft: NewsletterDraft | null) {
  try {
    if (draft) localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    else localStorage.removeItem(DRAFT_KEY)
  } catch {
    // ignore: the draft just won't be kept
  }
}

function NewsletterForm({ info }: { info: NewsletterInfo }) {
  const navigate = useNavigate()
  const suggested = useMemo(() => info.comics.filter((c) => c.suggested).map((c) => c.id), [info.comics])
  const [draft] = useState(readDraft)
  const [restored, setRestored] = useState(Boolean(draft))
  const [subject, setSubject] = useState(draft?.subject ?? '')
  const [body, setBody] = useState(draft?.body ?? '')
  // Comics deleted since the draft was saved drop out
  const [picked, setPicked] = useState<string[]>(() => (draft ? draft.picked.filter((id) => info.comics.some((c) => c.id === id)) : suggested))
  const [showAll, setShowAll] = useState(false)
  const [tried, setTried] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState<'' | 'test' | 'send'>('')
  const [done, setDone] = useState<{ title: string; text: string } | null>(null)
  const [failure, setFailure] = useState('')
  const empty = !subject.trim() && !body.trim() && !picked.length
  const preview = useNewsletterPreview(subject, body, picked, empty)

  const subjectBad = tried && !subject.trim()
  const bodyBad = tried && !body.trim() && !picked.length
  const ready = Boolean(subject.trim() && (body.trim() || picked.length))
  const count = info.subscribers
  const shown = showAll ? info.comics : info.comics.slice(0, Math.max(COMICS_SHOWN, lastPickedIndex(info.comics, picked) + 1))

  // Keep what's written on this device until it's sent
  useEffect(() => {
    const changed = subject || body || picked.length !== suggested.length || picked.some((id) => !suggested.includes(id))
    writeDraft(changed ? { subject, body, picked } : null)
  }, [subject, body, picked, suggested])

  const toggle = (id: string) => setPicked((now) => (now.includes(id) ? now.filter((p) => p !== id) : [...now, id]))

  const startOver = () => {
    setSubject('')
    setBody('')
    setPicked(suggested)
    setTried(false)
    setRestored(false)
  }

  const run = async (kind: 'test' | 'send') => {
    setFailure('')
    setDone(null)
    setBusy(kind)
    const payload = { subject, body, comicIds: picked }
    try {
      if (kind === 'test') {
        const r = await api.post<{ to: string }>('/api/admin/newsletter/test', payload)
        setDone({ title: 'Test sent.', text: `Check ${r.to} to see how it looks.` })
        setBusy('')
      } else {
        const r = await api.post<{ recipients: number }>('/api/admin/newsletter/send', payload)
        writeDraft(null)
        const sent: SentNewsletter = { sentAt: new Date().toISOString(), subject, recipients: r.recipients, comics: picked.length }
        navigate('/artist/newsletter', { state: { sent } })
      }
    } catch (error) {
      setConfirm(false)
      setBusy('')
      setFailure(error instanceof ApiError && error.status ? error.message : 'Check your connection and try again.')
    }
  }

  const attempt = (kind: 'test' | 'send') => {
    setTried(true)
    if (!ready) return
    if (kind === 'send') setConfirm(true)
    else run('test')
  }

  return (
    <>
      <PageHead
        title="New newsletter"
        intro="Write a note, pick the comics to show, and send it to everyone who has subscribed. It comes from whatacomicallife.com and ends with an unsubscribe link. What you write is kept on this device until you send it."
      />
      <NewsletterStats subscribers={count} lastSent={info.sent[0]} />
      {!info.emailEnabled && <EmailNotReady />}
      {restored && (
        <Alert
          tone="info"
          title="Picked up where you left off."
          actions={
            <button type="button" className="btn sm" onClick={startOver} disabled={Boolean(busy)}>
              Start over
            </button>
          }
        >
          This is the newsletter you hadn't sent yet.
        </Alert>
      )}
      {done && <Alert tone="ok" title={done.title}>{done.text}</Alert>}
      {failure && <Alert title="The newsletter couldn't be sent.">{failure}</Alert>}
      {(subjectBad || bodyBad) && <Alert title="Add a subject, and a message or a comic, before sending." />}
      <div className="formfield">
        <label htmlFor="nl-subject" className="lbl">
          Subject
        </label>
        <input id="nl-subject" className={subjectBad ? 'field err' : 'field'} aria-invalid={subjectBad} value={subject} disabled={Boolean(busy)} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <ComicLinkField
        id="nl-body"
        label="Message"
        tall
        invalid={bodyBad}
        value={body}
        onChange={setBody}
        disabled={Boolean(busy)}
        inserts={[{ label: 'Insert first name', text: '{{first name}}' }]}
        hint={
          <>
            Goes at the top, above the comics. Uses Markdown: **bold**, *italic*, [words](https://link), # Heading, - list item, &gt; quote. Type [[ to link to one of your comics.
            <br />
            {'{{first name}}'} becomes each subscriber's first name, or "friend" if we don't have one. It works in the subject too. The preview and test email use "Alex".
          </>
        }
      />
      {preview.unknownLinks.length > 0 && (
        <Alert tone="info" title={`No comic matches ${preview.unknownLinks.map((l) => `[[${l}]]`).join(', ')}`}>
          {preview.unknownLinks.length === 1 ? 'It' : 'They'}'ll show as plain words. Use a comic's exact title, or type [[ and pick one.
        </Alert>
      )}
      <fieldset className="plain">
        <legend className="lbl">
          Comics to include <span className="hint">({picked.length} chosen)</span>
        </legend>
        <div className="hint">
          {info.sent.length ? 'Comics published since your last newsletter are already ticked.' : 'Comics published in the last month are already ticked.'} Each one shows as a card with its thumbnail, title, date, caption and a link to read it on the site.
        </div>
        {info.comics.length === 0 ? (
          <p className="hint">No published comics yet.</p>
        ) : (
          <ul className="list">
            {shown.map((c) => {
              const thumb = c.images[c.thumbnailIndex] ?? c.images[0]
              return (
                <li key={c.id}>
                  <label className="opt nl-comic">
                    <input type="checkbox" checked={picked.includes(c.id)} disabled={Boolean(busy)} onChange={() => toggle(c.id)} />
                    <div className="rowthumb">{thumb ? <img src={imageUrl(thumb.key)} alt="" loading="lazy" /> : 'No image'}</div>
                    <span>
                      <strong>{c.title || '[Untitled comic]'}</strong>
                      <span className="hint">
                        Happened {formatDate(c.date)}
                        {c.lastSentAt && ` · In the ${formatDate(c.lastSentAt)} newsletter`}
                      </span>
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
        {shown.length < info.comics.length && (
          <button type="button" className="btn self-start" onClick={() => setShowAll(true)}>
            Show older comics ({info.comics.length - shown.length} more)
          </button>
        )}
      </fieldset>
      <div className="vstack gap-sm">
        <h2 className="h3">Preview</h2>
        <div className="hint">How it looks in an inbox. "Send me a test" shows the real thing.</div>
        <div className="nl-preview" aria-busy={preview.loading}>
          {empty ? (
            <p className="hint">Write a subject or message, or tick a comic, to see the preview.</p>
          ) : preview.html ? (
            <EmailFrame html={preview.html} title="Newsletter preview" />
          ) : (
            <p className="hint">{preview.failed ? "The preview couldn't be loaded. It'll try again when you make a change." : 'Loading the preview…'}</p>
          )}
        </div>
      </div>
      <div className="btns bar">
        <button type="button" className="btn" disabled={Boolean(busy)} onClick={() => attempt('test')}>
          {busy === 'test' && <span className="spin" aria-hidden="true" />}
          Send me a test
        </button>
        <button type="button" className="btn solid" disabled={Boolean(busy)} onClick={() => attempt('send')}>
          Send to all subscribers
        </button>
      </div>
      {confirm && (
        <Dialog labelledBy="ns-title" alert onClose={() => setConfirm(false)}>
          <h2 id="ns-title">Send to {plural(count, 'subscriber')}?</h2>
          <p>
            {picked.length ? `It includes ${plural(picked.length, 'comic')}. ` : "It doesn't include any comics. "}
            Once it's sent, it can't be taken back.
          </p>
          <div className="btns end mt-sm">
            <button type="button" className="btn" onClick={() => setConfirm(false)} disabled={busy === 'send'}>
              Cancel
            </button>
            <button type="button" className="btn solid" onClick={() => run('send')} disabled={busy === 'send'}>
              {busy === 'send' && <span className="spin" aria-hidden="true" />}
              Send now
            </button>
          </div>
        </Dialog>
      )}
    </>
  )
}

/** Keep any ticked comic visible, even an older one */
function lastPickedIndex(list: NewsletterComic[], picked: string[]): number {
  let last = -1
  list.forEach((c, i) => {
    if (picked.includes(c.id)) last = i
  })
  return last
}

/** A sent time as the artist's own calendar day */
function localDay(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** An email shown in a frame, as tall as its content so the page scrolls instead of the frame */
function EmailFrame({ html, title }: { html: string; title: string }) {
  return <iframe title={title} srcDoc={html} sandbox="allow-same-origin allow-popups" onLoad={fitToContent} />
}

function fitToContent(e: SyntheticEvent<HTMLIFrameElement>) {
  const frame = e.currentTarget
  const doc = frame.contentDocument
  if (!doc) return
  // Links in the preview open in a new tab instead of inside the frame
  const base = doc.createElement('base')
  base.target = '_blank'
  doc.head?.appendChild(base)
  // Measure the email itself: the page's own height never shrinks below the frame's
  const fit = () => (frame.style.height = `${Math.max(120, doc.body.scrollHeight)}px`)
  fit()
  doc.querySelectorAll('img').forEach((img) => img.addEventListener('load', fit))
}

/** The email as the server builds it, refreshed shortly after typing stops */
function useNewsletterPreview(subject: string, body: string, comicIds: string[], empty: boolean) {
  const [state, setState] = useState<{ html: string; unknownLinks: string[]; loading: boolean; failed: boolean }>({
    html: '',
    unknownLinks: [],
    loading: true,
    failed: false,
  })
  const key = JSON.stringify({ subject, body, comicIds: [...comicIds].sort() })
  useEffect(() => {
    if (empty) {
      setState({ html: '', unknownLinks: [], loading: false, failed: false })
      return
    }
    let stale = false
    setState((s) => ({ ...s, loading: true }))
    const timer = setTimeout(async () => {
      try {
        const r = await api.post<{ html: string; unknownLinks: string[] }>('/api/admin/newsletter/preview', JSON.parse(key))
        if (!stale) setState({ html: r.html, unknownLinks: r.unknownLinks, loading: false, failed: false })
      } catch {
        if (!stale) setState((s) => ({ ...s, loading: false, failed: !s.html }))
      }
    }, 600)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [key, empty])
  return state
}

/** One sent newsletter, as it looks today */
export function SentNewsletterPage() {
  const { sentAt = '' } = useParams()
  const load = useLoad(
    (signal) =>
      api.get<{ subject: string; sentAt: string; recipients: number; html: string; hasMessage: boolean }>(`/api/admin/newsletter/sent/${encodeURIComponent(sentAt)}`, signal),
    [sentAt]
  )
  useTitle(load.state === 'ready' ? load.data.subject || 'Sent newsletter' : 'Sent newsletter')
  return (
    <ArtistLayout>
      <div className="stack">
        <BackLink to="/artist/newsletter">All newsletters</BackLink>
        {load.state === 'loading' && (
          <Loading label="Loading the newsletter">
            <div className="vstack gap-md">
              <Sk w="50%" h={44} />
              <Sk h={400} />
            </div>
          </Loading>
        )}
        {load.state === 'error' &&
          (load.error.status === 404 ? (
            <EmptyState title="We couldn't find that newsletter" actions={<div className="btns"><Link className="btn solid" to="/artist/newsletter">Go to Newsletter</Link></div>}>
              The link may be wrong.
            </EmptyState>
          ) : (
            <LoadError title="This newsletter couldn't be loaded" retry={load.retry} />
          ))}
        {load.state === 'ready' && (
          <>
            <PageHead title={load.data.subject || '[No subject]'} intro={`Sent ${formatDate(localDay(load.data.sentAt))} to ${plural(load.data.recipients, 'subscriber')}`} />
            {!load.data.hasMessage && <Alert tone="info" title="The message wasn't saved for this one.">Newsletters sent before October 16, 2026 only kept the subject and comics.</Alert>}
            <div className="nl-preview">
              <EmailFrame html={load.data.html} title="Sent newsletter" />
            </div>
          </>
        )}
      </div>
    </ArtistLayout>
  )
}

// ---------- Settings ----------

function SettingsForm({ initial, tags }: { initial: SiteSettings; tags: string[] }) {
  const { reload } = useSettings()
  const [cats, setCats] = useState(initial.sidebarTags)
  const [pick, setPick] = useState('')
  const [heading, setHeading] = useState(initial.about.heading)
  const [text, setText] = useState(initial.about.text)
  const uploads = useImageUploads(initial.about.images)
  const titleUpload = useImageUploads(initial.titleImage ? [initial.titleImage] : [], 1)
  const [state, setState] = useState<'idle' | 'busy' | 'saved' | 'failed'>('idle')
  const options = useMemo(() => tags.filter((t) => !cats.includes(t)), [tags, cats])

  const move = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= cats.length) return
    const copy = cats.slice()
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
    setCats(copy)
  }

  const save = async () => {
    setState('busy')
    try {
      await api.put('/api/admin/settings', { sidebarTags: cats, about: { heading, text, images: uploads.saved }, titleImage: titleUpload.saved[0] ?? null })
      setState('saved')
      reload()
    } catch {
      setState('failed')
    }
  }

  return (
    <div className="stack lg">
      <PageHead title="Settings" />
      {state === 'saved' && (
        <Alert tone="ok" title="Settings saved.">
          The sidebar, the title image and the About page are updated.
        </Alert>
      )}
      {state === 'failed' && (
        <Alert title="Settings couldn't be saved" actions={<button type="button" className="btn sm" onClick={save}>Try again</button>}>
          Your changes are still here. Check your connection and try again.
        </Alert>
      )}

      <section aria-labelledby="ti-title" className="vstack gap-lg">
        <div className="vstack gap-xs">
          <h2 id="ti-title" className="h2">
            Title image <span className="hint">(optional)</span>
          </h2>
          <div className="hint">Shows above “What a / comical life” in the sidebar and on the login page. It’s left out of the phone top bar to keep it small.</div>
        </div>
        <ImageList uploads={titleUpload} idPrefix="title" />
      </section>

      <section aria-labelledby="fc-title" className="vstack gap-lg">
        <div className="vstack gap-xs">
          <h2 id="fc-title" className="h2">
            Sidebar tags
          </h2>
          <div className="hint">
            These show in the sidebar, in this order. Rename tags or add descriptions in <Link to="/artist/tags">Manage tags</Link>.
          </div>
        </div>
        {cats.length === 0 && (
          <div className="hint strong">
            No sidebar tags. The sidebar shows only Home, About and Contact.
          </div>
        )}
        <ol className="list gap-sm">
          {cats.map((c, i) => (
            <li key={c} className="row">
              <strong className="grow text-md">{c}</strong>
              <div className="hstack gap-sm">
                <button type="button" className="iconbtn" aria-label={`Move ${c} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                  <Icon name="up" />
                </button>
                <button type="button" className="iconbtn" aria-label={`Move ${c} down`} disabled={i === cats.length - 1} onClick={() => move(i, 1)}>
                  <Icon name="down" />
                </button>
                <button type="button" className="iconbtn" aria-label={`Remove ${c}`} onClick={() => setCats(cats.filter((x) => x !== c))}>
                  <Icon name="bin" />
                </button>
              </div>
            </li>
          ))}
        </ol>
        <div className="formfield">
          <label htmlFor="add-cat" className="lbl">
            Add a tag
          </label>
          <div className="hstack gap-md">
            <select id="add-cat" className="field grow" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Choose a tag</option>
              {options.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={!pick}
              onClick={() => {
                setCats([...cats, pick])
                setPick('')
              }}
            >
              Add
            </button>
          </div>
          <div className="hint">Lists every tag used on a comic.</div>
        </div>
      </section>

      <section aria-labelledby="ab-title" className="vstack gap-xl">
        <div className="hstack baseline between gap-sm-lg">
          <h2 id="ab-title" className="h2">
            About page
          </h2>
          <Link to="/about" className="strong">
            View About page
          </Link>
        </div>
        <div className="formfield">
          <label htmlFor="ab-head" className="lbl">
            Heading
          </label>
          <input id="ab-head" className="field" value={heading} onChange={(e) => setHeading(e.target.value)} />
        </div>
        <div className="formfield">
          <label htmlFor="ab-text" className="lbl">
            Text
          </label>
          <textarea id="ab-text" className="field medium" value={text} onChange={(e) => setText(e.target.value)} aria-describedby="ab-text-hint" />
          <div id="ab-text-hint" className="hint">
            Leave a blank line between paragraphs.
          </div>
        </div>
        <div className="vstack gap-md">
          <div className="lbl">
            Images <span className="hint">(optional)</span>
          </div>
          <ImageList uploads={uploads} idPrefix="about" />
          <div className="hint">The first image shows under the heading. The rest go between paragraphs, in this order.</div>
        </div>
      </section>

      <div className="btns bar">
        <button type="button" className="btn solid" disabled={state === 'busy' || uploads.busy || titleUpload.busy} onClick={save}>
          {state === 'busy' && <span className="spin" aria-hidden="true" />}
          Save settings
        </button>
      </div>
    </div>
  )
}

// ---------- Instagram ----------

function InstagramSection() {
  const load = useLoad((signal) => api.get<InstagramConnection>('/api/admin/instagram', signal), [])
  const [conn, setConn] = useState<InstagramConnection | null>(null)
  const [token, setToken] = useState('')
  const [state, setState] = useState<'idle' | 'busy'>('idle')
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; title: string; text?: string } | null>(null)
  const current = conn ?? (load.state === 'ready' ? load.data : null)

  const run = async (action: () => Promise<InstagramConnection & { ok?: boolean; problem?: string }>, done: (r: InstagramConnection & { ok?: boolean; problem?: string }) => void) => {
    setState('busy')
    setNote(null)
    try {
      const res = await action()
      setConn(res)
      done(res)
    } catch (error) {
      setNote({ tone: 'error', title: "That didn't work.", text: error instanceof ApiError && error.status ? error.message : 'Check your connection and try again.' })
    } finally {
      setState('idle')
    }
  }
  const connect = () =>
    run(
      () => api.put('/api/admin/instagram', { token }),
      (r) => {
        setToken('')
        setNote({ tone: 'ok', title: `Connected to @${r.username}.`, text: 'You can now post comics from the "Your comic is live" screen or Manage comics.' })
      }
    )
  const test = () =>
    run(
      () => api.post('/api/admin/instagram/test'),
      (r) => setNote(r.ok ? { tone: 'ok', title: 'The connection works.' } : { tone: 'error', title: 'The connection has a problem.', text: r.problem })
    )
  const disconnect = () => run(() => api.del('/api/admin/instagram'), () => setNote({ tone: 'ok', title: 'Instagram disconnected.' }))

  return (
    <section aria-labelledby="ig-set-title" className="vstack gap-lg">
      <div className="vstack gap-xs">
        <h2 id="ig-set-title" className="h2">
          Instagram
        </h2>
        <div className="hint">Lets the site post your comics to Instagram as a carousel, with the caption, where to read it and up to 5 tags as hashtags.</div>
      </div>
      {load.state === 'loading' && (
        <Loading label="Loading the Instagram connection">
          <Sk h={44} />
        </Loading>
      )}
      {load.state === 'error' && <LoadError title="The Instagram connection couldn't be loaded" retry={load.retry} />}
      {note && (
        <Alert tone={note.tone} title={note.title}>
          {note.text}
        </Alert>
      )}
      {current?.connected && (
        <>
          <div className="hint strong">
            Connected to @{current.username} since {formatDate(current.connectedAt ?? '')}. The site renews the login every week.
          </div>
          <div className="btns">
            <button type="button" className="btn" disabled={state === 'busy'} onClick={test}>
              Test connection
            </button>
            <button type="button" className="btn" disabled={state === 'busy'} onClick={disconnect}>
              Disconnect
            </button>
          </div>
        </>
      )}
      {current && !current.connected && (
        <>
          <ol className="body-text sm steps">
            <li>
              Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> and open your app.
            </li>
            <li>
              Open <strong>Instagram → API setup with Instagram login</strong>, then under <strong>Generate access tokens</strong>, add your Instagram account.
            </li>
            <li>Tap <strong>Generate token</strong>, log in to Instagram if asked, and copy the token.</li>
            <li>Paste it below and tap Connect.</li>
          </ol>
          <div className="formfield">
            <label htmlFor="ig-token" className="lbl">
              Access token
            </label>
            <div className="hstack gap-md">
              <input
                id="ig-token"
                className="field grow"
                value={token}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setToken(e.target.value)}
              />
              <button type="button" className="btn solid" disabled={state === 'busy' || !token.trim()} onClick={connect}>
                {state === 'busy' && <span className="spin" aria-hidden="true" />}
                Connect
              </button>
            </div>
            <div className="hint">The token stays on the site's server. It's never shown again or shared.</div>
          </div>
        </>
      )}
    </section>
  )
}

// ---------- Facebook Page ----------

function FacebookSection() {
  const load = useLoad((signal) => api.get<FacebookConnection>('/api/admin/facebook', signal), [])
  const [conn, setConn] = useState<FacebookConnection | null>(null)
  const [token, setToken] = useState('')
  const [pages, setPages] = useState<Array<{ id: string; name: string }>>([])
  const [pageId, setPageId] = useState('')
  const [state, setState] = useState<'idle' | 'busy'>('idle')
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; title: string; text?: string } | null>(null)
  const current = conn ?? (load.state === 'ready' ? load.data : null)

  const run = async (action: () => Promise<FacebookConnection & { ok?: boolean; problem?: string }>, done: (r: FacebookConnection & { ok?: boolean; problem?: string }) => void) => {
    setState('busy')
    setNote(null)
    try {
      const res = await action()
      if (res.pages?.length) {
        setPages(res.pages)
        setPageId(res.pages[0].id)
        return
      }
      setConn(res)
      done(res)
    } catch (error) {
      setNote({ tone: 'error', title: "That didn't work.", text: error instanceof ApiError && error.status ? error.message : 'Check your connection and try again.' })
    } finally {
      setState('idle')
    }
  }
  const connect = () =>
    run(
      () => api.put('/api/admin/facebook', { token, pageId: pages.length ? pageId : undefined }),
      (r) => {
        setToken('')
        setPages([])
        setNote({ tone: 'ok', title: `Connected to ${r.pageName}.`, text: 'You can now post comics to the Page from the "Your comic is live" screen or Manage comics.' })
      }
    )
  const test = () =>
    run(
      () => api.post('/api/admin/facebook/test'),
      (r) => setNote(r.ok ? { tone: 'ok', title: 'The connection works.' } : { tone: 'error', title: 'The connection has a problem.', text: r.problem })
    )
  const disconnect = () => run(() => api.del('/api/admin/facebook'), () => setNote({ tone: 'ok', title: 'Facebook disconnected.' }))

  return (
    <section aria-labelledby="fb-set-title" className="vstack gap-lg">
      <div className="vstack gap-xs">
        <h2 id="fb-set-title" className="h2">
          Facebook Page
        </h2>
        <div className="hint">
          Lets the site post your comics to your Facebook Page, with all the panels, the caption and a clickable link. To show a
          post on your personal profile too, tap Share on it in Facebook.
        </div>
      </div>
      {load.state === 'loading' && (
        <Loading label="Loading the Facebook connection">
          <Sk h={44} />
        </Loading>
      )}
      {load.state === 'error' && <LoadError title="The Facebook connection couldn't be loaded" retry={load.retry} />}
      {note && (
        <Alert tone={note.tone} title={note.title}>
          {note.text}
        </Alert>
      )}
      {current?.connected && (
        <>
          <div className="hint strong">
            Connected to {current.pageName} since {formatDate(current.connectedAt ?? '')}. This login doesn't expire.
          </div>
          <div className="btns">
            <button type="button" className="btn" disabled={state === 'busy'} onClick={test}>
              Test connection
            </button>
            <button type="button" className="btn" disabled={state === 'busy'} onClick={disconnect}>
              Disconnect
            </button>
          </div>
        </>
      )}
      {current && !current.connected && (
        <>
          <ol className="body-text sm steps">
            <li>
              Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Graph API Explorer</a> and pick your app under <strong>Meta App</strong>.
            </li>
            <li>
              Add the permissions <strong>pages_show_list</strong>, <strong>pages_read_engagement</strong> and <strong>pages_manage_posts</strong>, then tap{' '}
              <strong>Generate Access Token</strong> and tick your comic's Page.
            </li>
            <li>
              Tap the <strong>ⓘ</strong> next to the token, then <strong>Open in Access Token Tool → Extend Access Token</strong>, and copy the new token.
            </li>
            <li>Paste it below and tap Connect.</li>
          </ol>
          {pages.length > 0 ? (
            <div className="formfield">
              <label htmlFor="fb-page" className="lbl">
                Which Page should comics go to?
              </label>
              <div className="hstack gap-md">
                <select id="fb-page" className="field grow" value={pageId} onChange={(e) => setPageId(e.target.value)}>
                  {pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn solid" disabled={state === 'busy'} onClick={connect}>
                  {state === 'busy' && <span className="spin" aria-hidden="true" />}
                  Connect
                </button>
              </div>
            </div>
          ) : (
            <div className="formfield">
              <label htmlFor="fb-token" className="lbl">
                Access token
              </label>
              <div className="hstack gap-md">
                <input id="fb-token" className="field grow" value={token} autoComplete="off" spellCheck={false} onChange={(e) => setToken(e.target.value)} />
                <button type="button" className="btn solid" disabled={state === 'busy' || !token.trim()} onClick={connect}>
                  {state === 'busy' && <span className="spin" aria-hidden="true" />}
                  Connect
                </button>
              </div>
              <div className="hint">The site keeps only your Page's own login, on its server. It's never shown again or shared.</div>
            </div>
          )}
        </>
      )}
    </section>
  )
}

// ---------- iPad shortcut ----------

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

function CopyField({ id, label, value }: { id: string; label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Copying can be blocked; the field can still be selected by hand
    }
  }
  return (
    <div className="formfield">
      <label htmlFor={id} className="lbl">
        {label}
      </label>
      <div className="hstack gap-md">
        <input id={id} className="field grow" value={value} readOnly onFocus={(e) => e.target.select()} />
        <button type="button" className="btn" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

function ShortcutSection() {
  const load = useLoad((signal) => api.get<{ createdAt: string | null }>('/api/admin/shortcut', signal), [])
  const [createdAt, setCreatedAt] = useState<string | null | undefined>(undefined)
  const [newKey, setNewKey] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle')
  const current = createdAt !== undefined ? createdAt : load.state === 'ready' ? load.data.createdAt : null

  const run = async (action: () => Promise<{ createdAt: string | null; key?: string }>) => {
    setState('busy')
    try {
      const res = await action()
      setCreatedAt(res.createdAt)
      setNewKey(res.key ?? '')
      setState('idle')
    } catch {
      setState('failed')
    }
  }
  const create = () => run(() => api.post('/api/admin/shortcut'))
  const turnOff = () => run(() => api.del('/api/admin/shortcut'))

  return (
    <section aria-labelledby="sc-title" className="vstack gap-lg">
      <div className="vstack gap-xs">
        <h2 id="sc-title" className="h2">
          iPad shortcut
        </h2>
        <div className="hint">
          Lets the "Send to comic site" shortcut turn images from the share sheet into a draft. Its key can only add
          drafts. It can't publish, change or delete anything.
        </div>
      </div>
      {load.state === 'loading' && (
        <Loading label="Loading the shortcut settings">
          <Sk h={44} />
        </Loading>
      )}
      {load.state === 'error' && <LoadError title="The shortcut key couldn't be loaded" retry={load.retry} />}
      {state === 'failed' && (
        <Alert title="That didn't work">Check your connection and try again.</Alert>
      )}
      {load.state === 'ready' && (
        <>
          {newKey ? (
            <Alert tone="ok" title="Key created">
              Copy it into the shortcut now. It won't be shown again.
            </Alert>
          ) : (
            <div className="hint strong">
              {current ? `The shortcut is on. Its key was created on ${formatDate(current)}.` : 'The shortcut is off. Create a key to turn it on.'}
            </div>
          )}
          {newKey && <CopyField id="sc-key" label="Upload key" value={newKey} />}
          <CopyField id="sc-upload" label="Upload address" value={`${API_BASE}/api/shortcut/uploads`} />
          <CopyField id="sc-draft" label="Draft address" value={`${API_BASE}/api/shortcut/drafts`} />
          <div className="btns">
            <button type="button" className="btn" disabled={state === 'busy'} onClick={create}>
              {current ? 'Replace key' : 'Create key'}
            </button>
            {current && (
              <button type="button" className="btn" disabled={state === 'busy'} onClick={turnOff}>
                Turn off
              </button>
            )}
          </div>
          {current && <div className="hint">Replacing the key or turning it off stops the shortcut until you paste a new key into it.</div>}
        </>
      )}
    </section>
  )
}

export function SettingsPage() {
  useTitle('Settings')
  const load = useLoad(
    (signal) =>
      Promise.all([
        api.get<SiteSettings>('/api/admin/settings', signal),
        api.get<{ items: Array<{ name: string }> }>('/api/admin/tags', signal),
      ]),
    []
  )
  return (
    <ArtistLayout>
      {load.state === 'loading' && <FormSkeleton />}
      {load.state === 'error' && <LoadError title="Settings couldn't be loaded" retry={load.retry} />}
      {load.state === 'ready' && (
        <div className="stack lg">
          <SettingsForm initial={load.data[0]} tags={load.data[1].items.map((t) => t.name)} />
          <InstagramSection />
          {FACEBOOK_POSTING && <FacebookSection />}
          <ShortcutSection />
        </div>
      )}
    </ArtistLayout>
  )
}
